import { parseISO, parse, format, isValid, setISOWeek, startOfISOWeek, Locale } from 'date-fns';
import { uk } from 'date-fns/locale';

/** Returns true if the string looks like an ISO date/datetime or year-month. */
export function isDateLike(val: string): boolean {
  return /^\d{4}-\d{2}/.test(val);
}

function getDateLocale(): Locale | undefined {
  const lng = localStorage.getItem('i18nextLng') ?? '';
  if (['ua-UK', 'ru-RU', 'ru', 'uk'].includes(lng)) return uk;
  return undefined; // date-fns default (en-US); European order enforced by format string
}

/**
 * Formats an ISO-like date string into a compact localised label (day-month-year order).
 *   2024-01-15  →  15 Jan '24  /  15 січ '24 (uk)
 *   2024-01-15T10:30:00Z  →  same
 *   2024-01     →  Jan '24  /  січ '24 (uk)
 *   2024        →  2024 (unchanged)
 */
export function formatDateCategory(val: string | number): string {
  // A widget's stored echartsOption can override the axis to a value/numeric
  // axis while this formatter survives the merge — echarts then passes a
  // number, so bail out before any string method is called (OSAVUL-FRONTEND-GF).
  if (typeof val !== 'string') {
    return String(val ?? '');
  }

  const locale = getDateLocale();
  const opts = locale ? { locale } : undefined;

  const weekMatch = val.match(/^(\d{4})-W(\d{2})$/);
  if (weekMatch) {
    const d = startOfISOWeek(setISOWeek(new Date(Number(weekMatch[1]), 0, 4), Number(weekMatch[2])));
    return isValid(d) ? format(d, "d MMM ''yy", opts) : val;
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
    const d = parseISO(val);
    return isValid(d) ? format(d, "d MMM ''yy", opts) : val;
  }
  if (/^\d{4}-\d{2}$/.test(val)) {
    const d = parse(val, 'yyyy-MM', new Date());
    return isValid(d) ? format(d, "MMM ''yy", opts) : val;
  }
  return val;
}

/**
 * Escapes HTML-significant characters in untrusted strings before they are
 * interpolated into an ECharts tooltip/label formatter's return value. ECharts
 * renders those strings as innerHTML, and chart data (source/actor/channel
 * names, axis labels) is attacker-controlled, so every dynamic value must pass
 * through this before concatenation. Do NOT wrap ECharts-provided markup such
 * as `params.marker`.
 */
export function escapeHtml(val: unknown): string {
  return String(val ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Shared ECharts tooltip box style — apply to every tooltip config for visual consistency. */
export const ECHARTS_TOOLTIP_STYLE = {
  backgroundColor: '#fff',
  borderColor: '#e1e5ee',
  borderWidth: 1,
  padding: [8, 12] as [number, number],
  borderRadius: 6,
  textStyle: { color: '#2a324b', fontSize: 14 },
};

/** Formats a number with K / M / B suffix for chart labels. */
export function formatNumber(val: number): string {
  if (Math.abs(val) >= 1_000_000_000) return (val / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'b';
  if (Math.abs(val) >= 1_000_000) return (val / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'm';
  if (Math.abs(val) >= 1_000) return (val / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return Number.isInteger(val) ? String(val) : val.toFixed(2);
}

/**
 * Deep-merge an ECharts override object into a base option.
 * Arrays are merged element-by-element (each item deep-merged by index),
 * so e.g. `series: [{ smooth: false }]` only overrides the first series entry.
 */
export function mergeEChartsOption(
  base: Record<string, any>,
  override?: Record<string, unknown>
): Record<string, any> {
  if (!override) return base;

  const result = { ...base };
  for (const key of Object.keys(override)) {
    const baseVal = result[key];
    const overVal = override[key];

    if (Array.isArray(baseVal) && Array.isArray(overVal)) {
      result[key] = baseVal.map((item: any, i: number) => {
        if (i >= overVal.length) return item;
        const patch = overVal[i];
        if (
          item && typeof item === 'object' && !Array.isArray(item) &&
          patch && typeof patch === 'object' && !Array.isArray(patch)
        ) {
          return mergeEChartsOption(item, patch as Record<string, unknown>);
        }
        return patch;
      });
      // Append extra items from override if it's longer than base
      if (overVal.length > baseVal.length) {
        result[key] = result[key].concat(overVal.slice(baseVal.length));
      }
    } else if (
      overVal && typeof overVal === 'object' && !Array.isArray(overVal) &&
      baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal)
    ) {
      result[key] = mergeEChartsOption(
        baseVal as Record<string, any>,
        overVal as Record<string, unknown>
      );
    } else {
      result[key] = overVal;
    }
  }
  return result;
}
